import { useCallback, useEffect, useMemo, useState, Fragment } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import GoldSectionLayout from "../components/GoldSectionLayout";
import {
  Trophy, Layers, Calendar, Target, Clock, Goal, Timer,
  TrendingUp, Activity, ChevronRight,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_TABS = [
  { id: "all",           label: "All",            icon: Layers,      value: null },
  { id: "main",          label: "Main",           icon: Target,      value: "main" },
  { id: "halftime",      label: "Halftime",       icon: Clock,       value: "halftime" },
  { id: "double_chance", label: "Double Chance",  icon: Layers,      value: "double_chance" },
  { id: "btts",          label: "BTTS",           icon: Goal,        value: "btts" },
  { id: "goals",         label: "Goals O/U",      icon: TrendingUp,  value: "goals" },
  { id: "specials",      label: "Specials",       icon: Timer,       value: "specials" },
];

const rateTone = (r) =>
  r === null || r === undefined ? "text-amber-500/40"
  : r >= 70 ? "text-emerald-300"
  : r >= 55 ? "text-amber-200"
  : r >= 45 ? "text-amber-400"
  : "text-red-300";

const rateBar = (r) =>
  r === null || r === undefined ? "bg-neutral-700"
  : r >= 70 ? "bg-emerald-400"
  : r >= 55 ? "bg-amber-300"
  : r >= 45 ? "bg-amber-500"
  : "bg-red-400";

function StatTile({ label, value, hint, tone = "amber", testid }) {
  const palette = {
    amber: "bg-amber-500/10 border-amber-500/30 text-amber-100",
    emerald: "bg-emerald-500/10 border-emerald-500/30 text-emerald-100",
    neutral: "bg-neutral-500/10 border-neutral-500/25 text-neutral-200",
  };
  return (
    <div
      className={`rounded-xl border px-4 py-3.5 ${palette[tone]}`}
      data-testid={testid}
    >
      <div className="text-[10px] uppercase tracking-[0.22em] opacity-70 font-bold">{label}</div>
      <div className="text-2xl sm:text-3xl font-black tabular-nums leading-tight mt-1">
        {value === null || value === undefined ? "—" : value}
      </div>
      {hint && <div className="text-[10px] opacity-70 mt-1 font-tabular">{hint}</div>}
    </div>
  );
}

export default function GoldAccuracyPage() {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState(30);
  const [category, setCategory] = useState(null);
  const [minSamples, setMinSamples] = useState(1);
  const [expanded, setExpanded] = useState(null);
  const [search, setSearch] = useState("");

  const fetchData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/gold/accuracy/overview`, {
        params: { days, category: category || undefined, min_samples: minSamples },
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [days, category, minSamples, token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const g = data?.global || {};
  const cats = data?.by_category || {};
  const filteredLeagues = useMemo(() => {
    const rows = data?.by_league || [];
    if (!search.trim()) return rows;
    const q = search.trim().toLowerCase();
    return rows.filter(
      (L) =>
        (L.league_name || "").toLowerCase().includes(q) ||
        (L.country || "").toLowerCase().includes(q)
    );
  }, [data, search]);

  return (
    <GoldSectionLayout
      title="Accuracy Tracker"
      subtitle="Rolling hit-rate across every GOLD schedule fixture and all its graded markets. Click any league to expand the per-market breakdown."
      testid="gold-accuracy-page"
    >
      {/* Filters row */}
      <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="gold-accuracy-filters">
        <div className="flex items-center gap-1" data-testid="gold-accuracy-days-tabs">
          <span className="text-[10px] uppercase tracking-widest text-amber-500/60 mr-1">Window</span>
          {[7, 14, 30, 60, 90, 180].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`px-2.5 py-1 text-[11px] rounded border transition-colors font-bold uppercase tracking-widest ${
                d === days
                  ? "bg-amber-500 text-black border-amber-500 shadow shadow-amber-500/40"
                  : "bg-black/40 text-amber-500/70 border-amber-500/25 hover:bg-amber-500/10"
              }`}
              data-testid={`gold-accuracy-days-${d}`}
            >
              {d}d
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1" data-testid="gold-accuracy-min-samples-tabs">
          <span className="text-[10px] uppercase tracking-widest text-amber-500/60 mr-1">Min</span>
          {[1, 5, 10, 25, 50].map((s) => (
            <button
              key={s}
              onClick={() => setMinSamples(s)}
              className={`px-2 py-1 text-[11px] rounded border transition-colors font-bold ${
                s === minSamples
                  ? "bg-amber-500 text-black border-amber-500"
                  : "bg-black/40 text-amber-500/70 border-amber-500/25 hover:bg-amber-500/10"
              }`}
              data-testid={`gold-accuracy-min-samples-${s}`}
            >
              ≥{s}
            </button>
          ))}
        </div>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search league or country…"
          data-testid="gold-accuracy-search"
          className="ml-auto bg-black/40 border border-amber-500/25 text-amber-100 placeholder:text-amber-500/40 text-xs rounded-full h-8 px-3 min-w-[220px] focus:outline-none focus:border-amber-400"
        />
      </div>

      {/* Category tabs */}
      <div className="flex gap-1.5 mb-6 flex-wrap" data-testid="gold-accuracy-category-tabs">
        {CATEGORY_TABS.map((c) => {
          const Icon = c.icon;
          const active = c.value === category;
          const kpiRow = c.value ? cats[c.value] : g;
          const rate = kpiRow?.hit_rate_pct;
          return (
            <button
              key={c.id}
              onClick={() => { setCategory(c.value); setExpanded(null); }}
              data-testid={`gold-accuracy-cat-${c.id}`}
              className={`flex items-center gap-2 px-3.5 py-2 rounded-full border transition-colors ${
                active
                  ? "bg-amber-500/25 text-amber-100 border-amber-400 shadow shadow-amber-500/20"
                  : "bg-black/40 text-amber-500/70 border-amber-500/25 hover:bg-amber-500/10"
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              <span className="text-[11px] font-bold uppercase tracking-widest">{c.label}</span>
              {rate !== null && rate !== undefined && (
                <span className={`text-[11px] font-tabular font-bold ${rateTone(rate)}`}>
                  {rate}%
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Global tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="gold-accuracy-stat-tiles">
        <StatTile
          label="Hit Rate"
          value={g.hit_rate_pct != null ? `${g.hit_rate_pct}%` : "—"}
          hint={`${g.hits ?? 0} of ${g.graded ?? 0} graded`}
          tone={(g.hit_rate_pct ?? 0) >= 70 ? "emerald" : "amber"}
          testid="gold-tile-hit-rate"
        />
        <StatTile
          label="Graded Picks"
          value={g.graded ?? 0}
          hint={`${g.total ?? 0} total incl. pending`}
          tone="amber"
          testid="gold-tile-graded"
        />
        <StatTile
          label="Leagues Tracked"
          value={data?.league_count ?? 0}
          hint={`≥${minSamples} settled markets`}
          tone="neutral"
          testid="gold-tile-leagues"
        />
        <StatTile
          label="Pending"
          value={g.pending ?? 0}
          hint="Awaiting settle"
          tone="neutral"
          testid="gold-tile-pending"
        />
      </div>

      {/* League table with expandable per-market breakdown */}
      <div
        className="rounded-xl border border-amber-500/20 bg-gradient-to-br from-amber-950/20 via-black to-black overflow-hidden"
        data-testid="gold-accuracy-league-card"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-amber-500/15">
          <div className="flex items-center gap-2">
            <Trophy className="w-4 h-4 text-amber-300" />
            <h2 className="text-sm font-bold tracking-widest uppercase text-amber-200">
              Accuracy by league
            </h2>
            {data?.league_count > 0 && (
              <span className="text-[11px] text-amber-500/60 font-tabular">
                · {filteredLeagues.length} shown
              </span>
            )}
          </div>
          <div className="text-[10px] text-amber-500/60 uppercase tracking-widest hidden sm:block">
            Click a row to expand per-market
          </div>
        </div>

        {loading && !data && (
          <div className="p-8 text-center text-amber-400 animate-pulse text-sm">Loading accuracy…</div>
        )}

        {data && filteredLeagues.length === 0 && (
          <div className="p-8 text-center text-amber-500/60 text-sm italic">
            No leagues match the current filters.
          </div>
        )}

        {filteredLeagues.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="gold-league-table">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-amber-500/70 border-b border-amber-500/15">
                  <th className="text-left font-bold py-2.5 pl-4 pr-3">League</th>
                  <th className="text-left font-bold py-2.5 pr-3 hidden sm:table-cell">Country</th>
                  <th className="text-right font-bold py-2.5 pr-3">Graded</th>
                  <th className="text-right font-bold py-2.5 pr-3">Hits</th>
                  <th className="text-right font-bold py-2.5 pr-3">Miss</th>
                  <th className="text-right font-bold py-2.5 pr-4">Hit %</th>
                </tr>
              </thead>
              <tbody>
                {filteredLeagues.map((L) => {
                  const key = `${L.league_name}|${L.country || ""}`;
                  const slug = (L.league_name || "unknown")
                    .toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
                  const isOpen = expanded === key;
                  const marketRows = L.by_market
                    ? Object.values(L.by_market).sort(
                        (a, b) =>
                          (b.hit_rate_pct ?? -1) - (a.hit_rate_pct ?? -1) ||
                          b.graded - a.graded
                      )
                    : [];
                  return (
                    <Fragment key={key}>
                      <tr
                        onClick={() => setExpanded(isOpen ? null : key)}
                        className="border-b border-amber-500/10 hover:bg-amber-500/[0.04] transition-colors cursor-pointer"
                        data-testid={`gold-league-row-${slug}`}
                      >
                        <td className="py-2.5 pl-4 pr-3 flex items-center gap-2 font-semibold text-amber-100">
                          <ChevronRight
                            className={`w-3.5 h-3.5 text-amber-500/60 transition-transform ${isOpen ? "rotate-90 text-amber-300" : ""}`}
                          />
                          <span className="truncate max-w-[240px]">{L.league_name}</span>
                        </td>
                        <td className="py-2.5 pr-3 text-amber-500/70 text-xs hidden sm:table-cell">
                          {L.country || "—"}
                        </td>
                        <td className="py-2.5 pr-3 text-right font-tabular text-amber-500/80">{L.graded}</td>
                        <td className="py-2.5 pr-3 text-right font-tabular text-emerald-300">{L.hits}</td>
                        <td className="py-2.5 pr-3 text-right font-tabular text-red-300/80">{L.misses}</td>
                        <td className="py-2.5 pr-4 text-right">
                          <span className={`font-bold font-tabular ${rateTone(L.hit_rate_pct)}`}>
                            {L.hit_rate_pct != null ? `${L.hit_rate_pct.toFixed(1)}%` : "—"}
                          </span>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr
                          className="bg-black/60 border-b border-amber-500/15"
                          data-testid={`gold-league-row-${slug}-markets`}
                        >
                          <td colSpan={6} className="px-4 py-4">
                            <div className="mb-3 flex items-center gap-2">
                              <Activity className="w-3.5 h-3.5 text-amber-400" />
                              <span className="text-[10px] uppercase tracking-[0.22em] text-amber-400 font-bold">
                                Per-market accuracy · {L.league_name}
                              </span>
                            </div>
                            {marketRows.length === 0 ? (
                              <p className="text-xs text-amber-500/60 italic">
                                No settled markets in this league for the current filters.
                              </p>
                            ) : (
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
                                {marketRows.map((m) => (
                                  <div
                                    key={m.market_type}
                                    className="space-y-1"
                                    data-testid={`gold-league-market-${slug}-${m.market_type.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                  >
                                    <div className="flex items-center justify-between text-xs">
                                      <span className="text-amber-100/90 truncate pr-3 font-medium">
                                        {m.market_type}
                                        <span className="ml-1.5 text-[9px] uppercase tracking-widest text-amber-500/50">
                                          {m.category}
                                        </span>
                                      </span>
                                      <div className="flex items-center gap-3 shrink-0 font-tabular">
                                        <span className="text-amber-500/70">
                                          {m.hits}/{m.graded}
                                        </span>
                                        <span className={`font-bold w-14 text-right ${rateTone(m.hit_rate_pct)}`}>
                                          {m.hit_rate_pct != null ? `${m.hit_rate_pct.toFixed(1)}%` : "—"}
                                        </span>
                                      </div>
                                    </div>
                                    <div className="h-1.5 bg-amber-500/10 rounded-full overflow-hidden">
                                      <div
                                        className={`h-full rounded-full transition-all ${rateBar(m.hit_rate_pct)}`}
                                        style={{ width: `${m.hit_rate_pct ?? 0}%` }}
                                      />
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Global by-market summary at the bottom (all leagues combined) */}
      {data?.by_market?.length > 0 && (
        <div
          className="mt-6 rounded-xl border border-amber-500/20 bg-black/40 p-4"
          data-testid="gold-accuracy-market-summary"
        >
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="w-4 h-4 text-amber-300" />
            <h2 className="text-sm font-bold tracking-widest uppercase text-amber-200">
              All-league market summary
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
            {data.by_market.slice(0, 24).map((m) => (
              <div key={m.market_type} className="space-y-1">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-amber-100/90 truncate pr-3 font-medium">
                    {m.market_type}
                    <span className="ml-1.5 text-[9px] uppercase tracking-widest text-amber-500/50">
                      {m.category}
                    </span>
                  </span>
                  <div className="flex items-center gap-3 shrink-0 font-tabular">
                    <span className="text-amber-500/70">{m.hits}/{m.graded}</span>
                    <span className={`font-bold w-14 text-right ${rateTone(m.hit_rate_pct)}`}>
                      {m.hit_rate_pct != null ? `${m.hit_rate_pct.toFixed(1)}%` : "—"}
                    </span>
                  </div>
                </div>
                <div className="h-1.5 bg-amber-500/10 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${rateBar(m.hit_rate_pct)}`}
                    style={{ width: `${m.hit_rate_pct ?? 0}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Daily trend footer */}
      {data?.by_day?.length > 0 && (
        <div
          className="mt-6 rounded-xl border border-amber-500/20 bg-black/40 p-4"
          data-testid="gold-accuracy-daily-trend"
        >
          <div className="flex items-center gap-2 mb-3">
            <Calendar className="w-4 h-4 text-amber-300" />
            <h2 className="text-sm font-bold tracking-widest uppercase text-amber-200">
              Daily trend
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="gold-daily-trend-table">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-amber-500/60 border-b border-amber-500/15">
                  <th className="text-left font-bold py-1.5 pr-3">Date</th>
                  <th className="text-right font-bold py-1.5 pr-3">Hits</th>
                  <th className="text-right font-bold py-1.5 pr-3">Miss</th>
                  <th className="text-right font-bold py-1.5 pr-3">Graded</th>
                  <th className="text-right font-bold py-1.5">Hit %</th>
                </tr>
              </thead>
              <tbody>
                {[...data.by_day].reverse().slice(0, 30).map((d) => (
                  <tr key={d.date} className="border-b border-amber-500/5 hover:bg-amber-500/[0.03]">
                    <td className="py-1.5 pr-3 text-amber-100">{d.date}</td>
                    <td className="py-1.5 pr-3 text-right text-emerald-300 font-tabular">{d.hits}</td>
                    <td className="py-1.5 pr-3 text-right text-red-300/80 font-tabular">{d.misses}</td>
                    <td className="py-1.5 pr-3 text-right text-amber-500/80 font-tabular">{d.graded}</td>
                    <td className={`py-1.5 text-right font-tabular font-bold ${rateTone(d.hit_rate_pct)}`}>
                      {d.hit_rate_pct != null ? `${d.hit_rate_pct}%` : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </GoldSectionLayout>
  );
}
